import {
  OrderGetResponseType,
  SupplierRelationshipGetResponseType,
  SupplierRelationshipFulfillmentType,
} from "@sellar-io/api-v2/types";

import { nanoid } from "nanoid";

import { createContext, Dispatch, useContext } from "react";

import type { ISODateString } from "../types/isoDate";

export interface ProductItem extends Omit<
  NonNullable<ContextOrderBasketType["OrderItems"]>[number]["Product"],
  "ProductPriceTier"
> {
  ProductPriceTier: {
    PriceTierId: number;
    price: number;
    fullPrice: number;
    discountable: boolean;
  } | null;
}

export interface BasketOrderItem extends Omit<
  OrderGetResponseType["OrderItems"][number],
  "id"
> {
  id: number | undefined;
  promotionRewardItemId?: number;
}

export interface OrderBasketType extends Omit<
  OrderGetResponseType,
  | "OrderItems"
  | "createdAt"
  | "id"
  | "updatedAt"
  | "DeliveryAddress"
  | "InvoiceAddress"
> {
  id: number;
  updatedAt: Date | null;
  OrderItems: BasketOrderItem[];
  SupplierId: number;
  RetailerId: number;
  OrderDiscounts?: {
    PriceTierDiscountId: number;
  }[];
  OrderComments?: { message: string }[];
}

export interface ContextOrderBasketType extends Partial<
  Omit<OrderBasketType, "OrderItems" | "RetailerId">
> {
  OrderItems?: BasketOrderItem[];
  RetailerId: number | null;
  idempotencyKey?: string;
  _fromOrderFlowToken?: boolean;
  sdBasketId?: number;
  sdBasketUuid?: string;
  expectedDeliveryDate?: ISODateString | null;
  orderCutoffDate?: string | null;
}

export type BasketContextState = {
  temporaryCartId: string;
  order: ContextOrderBasketType | null;
  connection?: SupplierRelationshipGetResponseType;
  loading?: boolean;
  modifyOrderItem?: (params: {
    productItem: ProductItem;
    quantity: number;
    applyEligiblePromotionRewards: (params: {
      order: ContextOrderBasketType;
    }) => ContextOrderBasketType;
  }) => void;
};

export type BasketContextAction =
  | { type: BasketContextActionEnum.RESET }
  | {
      type: BasketContextActionEnum.CHANGE_DISCOUNT;
      discount: {
        list: {
          PriceTierDiscountId: number;
        }[];
        totalVolumeDiscountAmount: number;
      };
    }
  | {
      type: BasketContextActionEnum.CHANGE_VAT_PERCENTAGE;
      basketVatPercentage: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_DELIVERY_CHARGE;
      deliveryCharge: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_CONNECTION;
      connection: SupplierRelationshipGetResponseType;
    }
  | {
      type: BasketContextActionEnum.CHANGE_SUPPLIER_RETAILER_IDS;
      RetailerId?: number;
      SupplierId?: number;
    }
  | {
      type: BasketContextActionEnum.CHANGE_CURRENCY;
      currency: "gbp";
    }
  | {
      type: BasketContextActionEnum.CHANGE_FULFILLMENT_TYPE;
      fulfillmentType: SupplierRelationshipFulfillmentType;
    }
  | {
      type: BasketContextActionEnum.CHANGE_ORDER;
      order: Partial<ContextOrderBasketType> | null;
    }
  | {
      type: BasketContextActionEnum.CHANGE_LOADING;
      loading: boolean;
    };

export enum BasketContextActionEnum {
  ADD_ITEM = "ADD_ITEM",
  REMOVE_ITEM = "REMOVE_ITEM",
  CHANGE_DISCOUNT = "CHANGE_DISCOUNT",
  RESET = "RESET",
  CHANGE_VAT_PERCENTAGE = "CHANGE_VAT_PERCENTAGE",
  CHANGE_DELIVERY_CHARGE = "CHANGE_DELIVERY_CHARGE",
  CHANGE_CURRENCY = "CHANGE_CURRENCY",
  CHANGE_FULFILLMENT_TYPE = "CHANGE_FULFILLMENT_TYPE",
  CHANGE_CONNECTION = "CHANGE_CONNECTION",
  CHANGE_ORDER = "CHANGE_ORDER",
  CHANGE_LOADING = "CHANGE_LOADING",
  CHANGE_SUPPLIER_RETAILER_IDS = "CHANGE_SUPPLIER_RETAILER_IDS",
}

export const initialState: BasketContextState = {
  temporaryCartId: nanoid(),
  order: {
    RetailerId: null,
    initiator: "retailer",
  },
};

export const reducer = (
  state: BasketContextState,
  action: BasketContextAction,
): BasketContextState => {
  switch (action.type) {
    case BasketContextActionEnum.RESET:
      return initialState;
    case BasketContextActionEnum.CHANGE_DISCOUNT:
      return {
        ...state,
        order: {
          ...state.order,
          discountAmount: action.discount.totalVolumeDiscountAmount,
          OrderDiscounts: action.discount.list,
          RetailerId: state.order?.RetailerId ?? null,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_VAT_PERCENTAGE:
      return {
        ...state,
        order: {
          ...state.order,
          vatPercentage: action.basketVatPercentage,
          RetailerId: state.order?.RetailerId ?? null,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_DELIVERY_CHARGE:
      return {
        ...state,
        order: {
          ...state.order,
          deliveryCharge: action.deliveryCharge,
          RetailerId: state.order?.RetailerId ?? null,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_CONNECTION:
      return {
        ...state,
        connection: action.connection,
      };
    case BasketContextActionEnum.CHANGE_SUPPLIER_RETAILER_IDS:
      return {
        ...state,
        order: {
          ...state.order,
          RetailerId: action.RetailerId ?? null,
          SupplierId: action.SupplierId,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_CURRENCY:
      return {
        ...state,
        order: {
          ...state.order,
          currency: action.currency,
          RetailerId: state.order?.RetailerId ?? null,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_FULFILLMENT_TYPE:
      return {
        ...state,
        order: {
          ...state.order,
          fulfillmentType: action.fulfillmentType || "direct",
          RetailerId: state.order?.RetailerId ?? null,
          idempotencyKey: state.order?.idempotencyKey,
        },
      };
    case BasketContextActionEnum.CHANGE_ORDER:
      return {
        ...state,
        order: {
          ...action.order,
          RetailerId: action.order?.RetailerId ?? null,
        },
      };
    case BasketContextActionEnum.CHANGE_LOADING:
      return {
        ...state,
        loading: action.loading,
      };
    default:
      console.error("INVALID ACTION");
      return state;
  }
};

export const BasketContext = createContext<
  | [state: BasketContextState, dispatch: Dispatch<BasketContextAction>]
  | undefined
>(undefined);

export const useBasketContext = () => {
  const context = useContext(BasketContext);
  if (context === undefined) {
    throw new Error("useBasketContext must be within BasketProvider");
  }
  return context;
};
